import { type KeyboardEvent, useMemo, useState } from 'react';
import { uniqueSorted } from '@/utils/array';
import { MS_PER_DAY } from '@/utils/date';

// Column-picker filter variant, extracted verbatim (matching semantics
// unchanged) from src/features/payout-accounts/payout-accounts-panel.tsx and
// src/features/user-console/tabs/auth/login-history-table.tsx, which had
// re-implemented this ~100-line filter engine + chip state as near-verbatim twins.
// This is a separate abstraction from src/components/filter-bar's dialog-per-column
// TableFilters<TRow> — that variant renders a pill+dialog per column; this one
// renders a single column-picker + value control with a shared chip row below.

export type ColumnFilterKind = 'text' | 'choice' | 'tokens' | 'number' | 'date';

/**
 * Sentinel chip value matching blank cells — empty/whitespace-only cell text, or (for
 * 'tokens' columns) a cell with no tokens at all. The parenthesised spelling doubles as
 * the chip and checkbox label, and cannot collide with real data in any current report.
 */
export const BLANK_FILTER_VALUE = '(Blanks)';

/** Sentinel chip value matching the opposite of BLANK_FILTER_VALUE: any cell with content. */
export const NON_BLANK_FILTER_VALUE = '(Non-blanks)';

/** How a 'tokens' column combines its selected values: match rows holding any of them, or all of them. */
export type TokenMatchMode = 'any' | 'all';

export interface ColumnFilterConfig<TRow> {
  field: string;
  title: string;
  filter: ColumnFilterKind;
  /** Filter-match haystack, and (for 'choice' columns) the source of dropdown options. */
  getText: (row: TRow) => string;
  /** Required for 'tokens' columns: the cell's individual values (e.g. a pipe-joined blob split apart). */
  getTokens?: (row: TRow) => string[];
}

export interface ActiveColumnFilter {
  field: string;
  value: string;
}

/**
 * Parse a 'number' filter chip value like `>1000` / `<0.5`; null when malformed.
 * The threshold accepts anything Number() accepts (`.5`, `1e3`, …) so the input
 * UI and this parser can never disagree about what is a valid chip.
 */
export const parseNumberFilter = (value: string): { op: '>' | '<'; threshold: number } | null => {
  const match = /^([<>])\s*(\S.*)$/.exec(value.trim());
  if (!match) return null;
  const threshold = Number(match[2]);
  return Number.isFinite(threshold) ? { op: match[1] as '>' | '<', threshold } : null;
};

const matchesNumberFilter = (cellText: string, values: string[]): boolean => {
  if (cellText.trim() === '') return false;
  const cell = Number(cellText);
  if (!Number.isFinite(cell)) return false;
  return values.some((value) => {
    const parsed = parseNumberFilter(value);
    if (!parsed) return false;
    return parsed.op === '>' ? cell > parsed.threshold : cell < parsed.threshold;
  });
};

const LAST_DAYS_PATTERN = /^last\s+(\d+)\s+days?$/i;
const DATE_RANGE_PATTERN = /^(?:from\s+(\d{4}-\d{2}-\d{2}))?\s*(?:to\s+(\d{4}-\d{2}-\d{2}))?$/i;

export type DateFilter = { kind: 'lastDays'; days: number } | { kind: 'range'; fromMs: number | null; toMs: number | null };

/** Epoch ms for a time on a UTC day, or null for impossible dates Date.parse would roll over (e.g. 02-30 -> 03-02). */
const parseUtcDay = (day: string, time: string): number | null => {
  const ms = Date.parse(`${day}T${time}`);
  if (Number.isNaN(ms) || !new Date(ms).toISOString().startsWith(day)) return null;
  return ms;
};

/**
 * Parse a 'date' filter chip: `last N days` (rolling window ending now), or
 * `from YYYY-MM-DD to YYYY-MM-DD` with either side optional. Range bounds are
 * inclusive whole UTC days. Null when malformed (unknown shape, zero-day window,
 * impossible calendar date, inverted range) — like parseNumberFilter, the input
 * UI validates through this same parser so an accepted chip always matches.
 */
export const parseDateFilter = (value: string): DateFilter | null => {
  const trimmed = value.trim();

  const lastDays = LAST_DAYS_PATTERN.exec(trimmed);
  if (lastDays) {
    const days = Number(lastDays[1]);
    return days > 0 ? { kind: 'lastDays', days } : null;
  }

  const range = DATE_RANGE_PATTERN.exec(trimmed);
  if (!range || (!range[1] && !range[2])) return null;
  const fromMs = range[1] ? parseUtcDay(range[1], '00:00:00.000Z') : null;
  const toMs = range[2] ? parseUtcDay(range[2], '23:59:59.999Z') : null;
  if ((range[1] && fromMs === null) || (range[2] && toMs === null)) return null;
  if (fromMs !== null && toMs !== null && fromMs > toMs) return null;
  return { kind: 'range', fromMs, toMs };
};

const matchesDateFilter = (cellText: string, values: string[], now: number): boolean => {
  const time = Date.parse(cellText.trim());
  if (Number.isNaN(time)) return false;
  return values.some((value) => {
    const parsed = parseDateFilter(value);
    if (!parsed) return false;
    if (parsed.kind === 'lastDays') return time >= now - parsed.days * MS_PER_DAY;
    return (parsed.fromMs === null || time >= parsed.fromMs) && (parsed.toMs === null || time <= parsed.toMs);
  });
};

/**
 * Values on the same column OR together; different columns AND together (legacy
 * BopDataTable semantics — the payout-accounts panel and login-history table both
 * cited this independently before the extraction). 'choice' columns match the cell
 * text EXACTLY; 'text' columns match by case-insensitive substring. This is
 * deliberate per-surface legacy parity (legacy 'multi-text' filter type -> exact
 * equality; legacy 'text' filter type -> substring), not accidental drift — do not
 * unify the two modes.
 *
 * 'tokens' columns match against getTokens(row): 'any' mode (default) keeps rows
 * holding at least one selected value; 'all' mode requires every selected value.
 * 'number' columns match `>X` / `<X` chips against the numeric cell value.
 * 'date' columns match `last N days` / `from X to Y` chips (see parseDateFilter)
 * against the parsed cell timestamp; unparsable cells never match a date chip.
 *
 * A BLANK_FILTER_VALUE chip matches blank cells on every kind, OR-ing with the
 * column's other chips like any value ('tokens' 'all' mode: an empty token list
 * satisfies the blank chip, so blank + concrete tokens deliberately matches nothing).
 * NON_BLANK_FILTER_VALUE is its mirror — alone on a column it drops the blank rows.
 *
 * `now` anchors the 'date' kind's rolling windows; tests pass a fixed value.
 */
export const applyColumnFilters = <TRow>(
  rows: TRow[],
  filters: ActiveColumnFilter[],
  configs: ColumnFilterConfig<TRow>[],
  tokenModes: Record<string, TokenMatchMode> = {},
  now: number = Date.now(),
): TRow[] => {
  if (filters.length === 0) return rows;
  const configsByField = new Map(configs.map((config) => [config.field, config]));
  const grouped = new Map<string, string[]>();
  for (const { field, value } of filters) {
    grouped.set(field, [...(grouped.get(field) ?? []), value]);
  }
  const groups = [...grouped.entries()];
  return rows.filter((row) =>
    groups.every(([field, values]) => {
      const config = configsByField.get(field);
      // Fail CLOSED, matching legacy: a filter on a field that has dropped out of the
      // current config set (e.g. an IBAN filter surviving a fiat->crypto data refresh)
      // must not silently start matching everything — legacy always resolved a real
      // matcher against the full static column list, so an empty cell for a now-absent
      // field correctly excluded every row instead of the chip becoming a silent no-op.
      if (!config) return false;
      const cellText = config.getText(row);
      const isBlank = cellText.trim() === '';
      const matchesSentinel = (values.includes(BLANK_FILTER_VALUE) && isBlank) || (values.includes(NON_BLANK_FILTER_VALUE) && !isBlank);
      if (config.filter === 'choice') return matchesSentinel || values.includes(cellText);
      if (config.filter === 'tokens') {
        const tokens = config.getTokens?.(row) ?? [];
        const matchesToken = (value: string) => {
          if (value === BLANK_FILTER_VALUE) return tokens.length === 0;
          if (value === NON_BLANK_FILTER_VALUE) return tokens.length > 0;
          return tokens.includes(value);
        };
        return tokenModes[field] === 'all' ? values.every(matchesToken) : values.some(matchesToken);
      }
      if (config.filter === 'number') return matchesSentinel || matchesNumberFilter(cellText, values);
      if (config.filter === 'date') return matchesSentinel || matchesDateFilter(cellText, values, now);
      const haystack = cellText.toLowerCase();
      return matchesSentinel || values.some((value) => value !== BLANK_FILTER_VALUE && value !== NON_BLANK_FILTER_VALUE && haystack.includes(value.toLowerCase()));
    }),
  );
};

export interface UseColumnFiltersResult<TRow> {
  filters: ActiveColumnFilter[];
  filterText: string;
  setFilterField: (field: string) => void;
  setFilterText: (text: string) => void;
  activeFilterConfig: ColumnFilterConfig<TRow> | undefined;
  /** Sorted distinct values for the active 'choice'/'tokens' column; empty for 'text' columns. */
  choiceOptions: string[];
  filteredData: TRow[] | undefined;
  addFilter: (field: string, rawValue: string) => void;
  /** Replace every filter on one column with `values` in a single update (a select-all toggle). */
  setFieldValues: (field: string, values: string[]) => void;
  removeFilter: (target: ActiveColumnFilter) => void;
  /** Drop every filter on one column (a column-header popover's Clear button). */
  clearField: (field: string) => void;
  submitTextFilter: () => void;
  handleFilterKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  clearAll: () => void;
  /** Any/all match mode per 'tokens' column; unset fields default to 'any'. */
  tokenModes: Record<string, TokenMatchMode>;
  setTokenMode: (field: string, mode: TokenMatchMode) => void;
}

/**
 * Column-picker filter state: one active "filter column" + value control, plus a
 * shared chip row of applied filters. `configs` must already be narrowed to the
 * filterable columns (callers with non-filterable columns, e.g. legacy filter
 * type 'none', filter those out before passing configs in here).
 */
export const useColumnFilters = <TRow>(data: TRow[] | undefined, configs: ColumnFilterConfig<TRow>[], defaultField?: string): UseColumnFiltersResult<TRow> => {
  const [filters, setFilters] = useState<ActiveColumnFilter[]>([]);
  const [filterField, setFilterField] = useState<string>(defaultField ?? configs[0]?.field ?? '');
  const [filterText, setFilterText] = useState('');
  const [tokenModes, setTokenModes] = useState<Record<string, TokenMatchMode>>({});

  const activeFilterConfig = configs.find((config) => config.field === filterField) ?? configs[0];

  // Multi-choice options are derived from the loaded data, sorted.
  const choiceOptions = useMemo(() => {
    if (activeFilterConfig?.filter === 'tokens') {
      return uniqueSorted((data ?? []).flatMap((row) => activeFilterConfig.getTokens?.(row) ?? []).filter(Boolean));
    }
    if (activeFilterConfig?.filter !== 'choice') return [];
    return uniqueSorted((data ?? []).map(activeFilterConfig.getText).filter(Boolean));
  }, [data, activeFilterConfig]);

  const filteredData = useMemo(() => (data ? applyColumnFilters(data, filters, configs, tokenModes) : undefined), [data, filters, configs, tokenModes]);

  const addFilter = (field: string, rawValue: string) => {
    const value = rawValue.trim();
    if (!value) return;
    setFilters((previous) => (previous.some((filter) => filter.field === field && filter.value === value) ? previous : [...previous, { field, value }]));
  };

  const setFieldValues = (field: string, values: string[]) => {
    const cleaned = uniqueSorted(values.map((value) => value.trim()).filter(Boolean));
    setFilters((previous) => [...previous.filter((filter) => filter.field !== field), ...cleaned.map((value) => ({ field, value }))]);
  };

  const removeFilter = (target: ActiveColumnFilter) => {
    setFilters((previous) => previous.filter((filter) => !(filter.field === target.field && filter.value === target.value)));
  };

  const clearField = (field: string) => {
    setFilters((previous) => previous.filter((filter) => filter.field !== field));
  };

  const setTokenMode = (field: string, mode: TokenMatchMode) => {
    setTokenModes((previous) => ({ ...previous, [field]: mode }));
  };

  const submitTextFilter = () => {
    if (!activeFilterConfig) return;
    addFilter(activeFilterConfig.field, filterText);
    setFilterText('');
  };

  const handleFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    submitTextFilter();
  };

  const clearAll = () => {
    setFilters([]);
    setTokenModes({});
  };

  return {
    filters,
    filterText,
    setFilterField,
    setFilterText,
    activeFilterConfig,
    choiceOptions,
    filteredData,
    addFilter,
    setFieldValues,
    removeFilter,
    clearField,
    submitTextFilter,
    handleFilterKeyDown,
    clearAll,
    tokenModes,
    setTokenMode,
  };
};
